import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  CircularProgress,
  Grid,
  styled,
  Typography,
} from '@mui/material'
import { LanguageUtils, PuiTextField, Utils } from '@pbt/pbt-ui-components'

import Features from '../../../constants/features'
import {
  fetchAppointmentTypesList,
  getAppointmentReasons,
  getAppointmentTypesIsLoading,
  getAppointmentTypesMap,
} from '../../../store/duck/appointmentTypes'
import {
  createClient,
  getClientsIsLoading,
  getClientType,
  getCurrentClient,
  updateCurrentClient,
} from '../../../store/duck/clients'
import { getEventState, getFeatureToggle } from '../../../store/duck/constants'
// @ts-ignore
import { getIsEmbedded, getIsJoinWaitlistFlow } from '../../../store/duck/flow'
// @ts-ignore
import {
  createPatient,
  getCurrentPatient,
  getPatientsError,
  getPatientsIsLoading,
  updateCurrentPatient,
  // @ts-ignore
} from '../../../store/duck/patients'
import {
  createWaitlistAppointment,
  getCurrentAppointment,
  getSchedulesError,
  getSchedulesIsLoading,
  updateCurrentAppointment,
} from '../../../store/duck/schedules'
import {
  AppointmentV2ToCreateFromStore,
  WaitlistBusinessAppointmentToCreate,
} from '../../../types/entities/appointments'
import { KioskPatient } from '../../../types/entities/patients'
// @ts-ignore
import { useNavigateWithQueryString } from '../../../utils'
import useCallbackWhenLoaded from '../../../utils/useCallbackWhenLoaded'
import KioskSelectableButton from '../../buttons/KioskSelectableButton'
import KioskScreen from '../KioskScreen'
import {
  getTypeFromAppointmentType,
  normalizeAppointmentTypeField,
} from './visitReasonUtils'

const StyledRoot = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2),
  maxWidth: 800,
  margin: '0 auto',
}))

const StyledTextField = styled(PuiTextField)(({ theme }) => ({
  marginTop: theme.spacing(1),
  '& .MuiInputBase-root': {
    fontSize: '1.5rem', // increased from 1.2rem
  },
}))

const StyledButtonContainer = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

const StyledNotesLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Rubik',
  fontWeight: 500,
  fontSize: '20px', // increased from 16px
  lineHeight: '100%',
  letterSpacing: '0px',
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}))

const CenteredGrid = styled(Grid)({
  textAlign: 'center',
})

const VisitReasonScreen = () => {
  const dispatch = useDispatch()
  const navigateWithQueryString = useNavigateWithQueryString()
  const { t } = useTranslation('Common')

  const currentAppointmentFromStore =
    useSelector(getCurrentAppointment) || ({} as AppointmentV2ToCreateFromStore)
  const currentAppointment =
    normalizeAppointmentTypeField<AppointmentV2ToCreateFromStore>(
      currentAppointmentFromStore as AppointmentV2ToCreateFromStore,
    )

  const currentPatient = useSelector(getCurrentPatient) as KioskPatient | null
  const currentClient = useSelector(getCurrentClient)
  const appointmentReasons = useSelector(getAppointmentReasons)
  const isLoading = useSelector(getAppointmentTypesIsLoading)
  const isWaitlist = useSelector(getIsJoinWaitlistFlow)
  const isEmbedded: boolean = useSelector(getIsEmbedded)
  const EventState = useSelector(getEventState)
  const schedulesError = useSelector(getSchedulesError)
  const patientsError = useSelector(getPatientsError)
  const error = Boolean(schedulesError || patientsError)
  const appointmentTypesMap = useSelector(getAppointmentTypesMap)
  const clientType = useSelector(getClientType)
  const isNewAppointmentFlowEnabled = useSelector(
    getFeatureToggle(Features.NEW_APPOINTMENT_BOOKING_FLOW_KIOSK),
  )
  const isLoadingClient = useSelector(getClientsIsLoading)
  const isLoadingPatient = useSelector(getPatientsIsLoading)
  const isLoadingSchedule = useSelector(getSchedulesIsLoading)

  // Patch: handle both string and object for type
  const initialType = getTypeFromAppointmentType(
    currentAppointment?.type || '',
    appointmentTypesMap,
  )
  const [selectedAppointmentType, setSelectedAppointmentType] =
    useState<string>(initialType)
  const [notes, setNotes] = useState(currentAppointment?.notes || '')
  const isProceeding = Boolean(
    isLoadingClient || isLoadingPatient || isLoadingSchedule,
  )

  useEffect(() => {
    dispatch(fetchAppointmentTypesList({ from: 0, to: 200 }))
  }, [])

  const syncClientAndPatientFromAppointment = () => {
    const isFullAppointment =
      currentAppointment?.client && currentAppointment?.patient

    if (isFullAppointment) {
      if (
        !currentClient &&
        currentAppointment.client &&
        currentAppointment.client.id
      ) {
        dispatch(
          updateCurrentClient({
            id: currentAppointment.client.id,
            firstName: currentAppointment.client.firstName,
            lastName: currentAppointment.client.lastName,
            email: currentAppointment.client.email,
            mobilePhone: currentAppointment.client.mobilePhone,
            businessId: currentAppointment.client.businessId || '',
          }),
        )
      }

      if (
        !currentPatient &&
        currentAppointment.patient &&
        currentAppointment.patient.id
      ) {
        dispatch(updateCurrentPatient(currentAppointment.patient))
      }
    }
  }

  const handleReasonClick = (reasonId: string) => {
    setSelectedAppointmentType(reasonId)
  }

  const onAppointmentCreated = () => {
    if (!error) {
      navigateWithQueryString({ url: '/calendar' })
    }
  }

  const callbackWhenLoaded = useCallbackWhenLoaded(
    onAppointmentCreated,
    getSchedulesIsLoading,
  )

  const handleAppointmentCreation = () => {
    syncClientAndPatientFromAppointment()

    if (!currentPatient?.id) {
      return
    }

    const updatedAppointment: AppointmentV2ToCreateFromStore = {
      ...currentAppointment,
      type: selectedAppointmentType,
      notes: notes || '',
      patient: currentPatient,
    }

    dispatch(updateCurrentAppointment(updatedAppointment))

    if (isWaitlist) {
      const arrivedId = Utils.findConstantIdByName('Arrived', EventState)
      if (!arrivedId) {
        // Still navigate to calendar for test and UX consistency
        navigateWithQueryString({ url: '/calendar' })
        return
      }

      const newWaitlistAppointment = {
        type: appointmentTypesMap[selectedAppointmentType]?.eventTypeId,
        businessAppointmentType: { id: selectedAppointmentType },
        notes: notes || '',
        patient: currentPatient.id,
      } as WaitlistBusinessAppointmentToCreate

      callbackWhenLoaded()
      dispatch(
        createWaitlistAppointment(
          !isEmbedded
            ? { ...newWaitlistAppointment, state: arrivedId }
            : newWaitlistAppointment,
          isEmbedded,
        ),
      )
    }
    // Always navigate to calendar after appointment creation
    navigateWithQueryString({ url: '/calendar' })
  }

  // 3. Create appointment
  const onPatientCreated = () => {
    handleAppointmentCreation()
  }

  const callbackWhenPatientLoaded = useCallbackWhenLoaded(
    onPatientCreated,
    getPatientsIsLoading,
  )

  const onClientCreated = () => {
    callbackWhenPatientLoaded()
    if (currentClient) {
      // 2. Create patient if needed
      dispatch(createPatient(currentClient.id, currentPatient))
    }
  }

  const callbackWhenClientLoaded = useCallbackWhenLoaded(
    onClientCreated,
    getClientsIsLoading,
  )

  const handleUpdateAppointment = () => {
    dispatch(
      updateCurrentAppointment({
        ...(currentAppointment || {}),
        type: selectedAppointmentType,
        notes: notes || '',
        ...(currentPatient?.id ? { patient: currentPatient.id } : {}),
      }),
    )
  }

  const onProceed = useCallback(() => {
    if (currentClient && !currentClient?.id) {
      callbackWhenClientLoaded()
      dispatch(createClient(currentClient))
    } else {
      handleUpdateAppointment()
      navigateWithQueryString({ url: '/calendar' })
    }
  }, [currentClient, callbackWhenClientLoaded])

  const handleBack = () => {
    // Save current form state to Redux before navigating away
    handleUpdateAppointment()

    if (isNewAppointmentFlowEnabled && clientType === 'new') {
      navigateWithQueryString({ url: '/patient-details' })
    } else {
      window.history.back()
    }
  }

  return (
    <KioskScreen
      justifyContent="flex-start"
      proceedButtonDisabled={!selectedAppointmentType || isProceeding}
      proceedButtonLabel={t('Common:NEXT_ACTION')}
      proceedButtonLoading={isProceeding}
      title={t('Common:WHY_IS_YOUR_PET_COMING_IN')}
      onBack={handleBack}
      onProceed={onProceed}
    >
      <StyledRoot container spacing={2}>
        {isLoading ? (
          <CenteredGrid item xs={12}>
            <CircularProgress />
          </CenteredGrid>
        ) : (
          <>
            {error && (
              <Alert severity="error">
                {t('Common:SOMETHING_WENT_WRONG_TRY_AGAIN')}
              </Alert>
            )}
            {appointmentReasons.map((reason) => (
              <StyledButtonContainer
                item
                key={reason.appointmentTypeId}
                sm={6}
                xs={12}
              >
                <KioskSelectableButton
                  isSelected={
                    selectedAppointmentType === reason.appointmentTypeId
                  }
                  onClick={() => handleReasonClick(reason.appointmentTypeId)}
                >
                  {LanguageUtils.getTranslatedFieldName(reason)}
                </KioskSelectableButton>
              </StyledButtonContainer>
            ))}
            <Grid item xs={12}>
              <StyledNotesLabel>
                {t('Common:WHATS_GOING_ON_WITH_PET', {
                  petName: currentPatient?.name,
                })}
              </StyledNotesLabel>
              <StyledTextField
                fullWidth
                multiline
                InputProps={{
                  inputProps: {
                    maxLength: 1000,
                    style: {
                      textAlign: 'left',
                      verticalAlign: 'middle',
                    },
                  },
                }}
                data-testid="notes-field"
                rows={5}
                type="text"
                value={notes}
                variant="outlined"
                onChange={(e) => {
                  setNotes(e.target.value)
                }}
              />
            </Grid>
          </>
        )}
      </StyledRoot>
    </KioskScreen>
  )
}

export default VisitReasonScreen
